/**
 * KeyboardCheatSheet — self-contained modal listing all keyboard shortcuts.
 *
 * Registers a global `?` keydown listener and toggles itself open/closed.
 * The listener is ignored when the active element is an input, textarea,
 * select, or contenteditable node so operators can type `?` normally.
 *
 * Mount once inside AppShell — no props required.
 */
import { useEffect, useState } from 'react';
import { Modal } from './ui/Modal';

// ── Shortcut catalogue ────────────────────────────────────────────────────────

interface Shortcut {
  readonly key: string;
  readonly label: string;
  readonly group: string;
}

const SHORTCUTS: ReadonlyArray<Shortcut> = [
  { group: 'Navigation', key: '?',   label: 'Open / close keyboard shortcuts' },
  { group: 'Navigation', key: 'Esc', label: 'Close modal or dismiss overlay' },
];

/** Build an ordered map of group → shortcuts (insertion order preserved). */
function buildGroups(
  shortcuts: ReadonlyArray<Shortcut>,
): Map<string, ReadonlyArray<Shortcut>> {
  const map = new Map<string, Shortcut[]>();
  for (const s of shortcuts) {
    const bucket = map.get(s.group) ?? [];
    bucket.push(s);
    map.set(s.group, bucket);
  }
  return map;
}

const GROUPS = buildGroups(SHORTCUTS);

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Returns true when the event target is a text-entry element. */
function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function KeyboardCheatSheet() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (isTextEntry(e.target)) return;
      if (e.key === '?') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title="Keyboard shortcuts"
      size="md"
    >
      <div className="flex flex-col gap-5">
        {Array.from(GROUPS.entries()).map(([group, shortcuts]) => (
          <div key={group}>
            <p className="mb-2 text-[10px] uppercase tracking-wider text-text-3 font-medium">
              {group}
            </p>
            <ul className="flex flex-col gap-1.5">
              {shortcuts.map((s) => (
                <li
                  key={`${s.group}-${s.key}-${s.label}`}
                  className="flex items-center justify-between gap-4"
                >
                  <span className="text-sm text-text-2">{s.label}</span>
                  <kbd className="shrink-0 text-xs font-mono rounded bg-surface-glass px-2 py-0.5 text-text-1 ring-1 ring-surface-stroke">
                    {s.key}
                  </kbd>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Modal>
  );
}
